import { Injectable } from "@angular/core";
import type {
  CollectionReturnValue,
  Core,
  ElementDefinition,
  StylesheetStyle,
} from "cytoscape";
import type { ElkNode } from "elkjs/lib/elk.bundled";
import {workflowMapLayout, type WorkflowMapPoint, type WorkflowMapPositions} from './workflow-map-layout';

import { workflowDecisionGateRequest } from "./workflow-read-only-graph.models";
import type {
  WorkflowReadOnlyGraph,
  WorkflowReadOnlyGraphDecisionGate,
  WorkflowReadOnlyGraphDecisionGateRequest,
  WorkflowReadOnlyGraphDecisionScopeRequest,
  WorkflowReadOnlyGraphEdge,
  WorkflowReadOnlyGraphNode,
  WorkflowReadOnlyGraphResponsibilityLane,
} from "./workflow-read-only-graph.models";

interface WorkflowGraphLayoutNode extends ElkNode {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface WorkflowGraphTapTarget {
  id: () => string;
  hasClass: (className: string) => boolean;
  data: (name: string) => unknown;
}

export interface WorkflowGraphSelectionCallbacks {
  positions?: WorkflowMapPositions;
  move?: (elementId:string,point:WorkflowMapPoint)=>void;
  select: (elementId: string) => void;
  clear: () => void;
  decisionGate?: (request: WorkflowReadOnlyGraphDecisionGateRequest) => void;
  decisionScope?: (request: WorkflowReadOnlyGraphDecisionScopeRequest) => void;
}

export type WorkflowGraphViewport = Core;
export type WorkflowGraphLayout = ElkNode;
export type WorkflowGraphLayoutProfile = "compact" | "overview" | "routed";

/** Local camera state only; it cannot move graph nodes or alter the definition. */
export interface WorkflowGraphCameraState {
  readonly zoom: number;
  readonly pan: { readonly x: number; readonly y: number };
}

export function validWorkflowGraphCameraState(value: WorkflowGraphCameraState | null | undefined): value is WorkflowGraphCameraState {
  return !!value && Number.isFinite(value.zoom) && value.zoom > 0 && !!value.pan
    && Number.isFinite(value.pan.x) && Number.isFinite(value.pan.y)
    && Math.abs(value.pan.x) <= 1_000_000 && Math.abs(value.pan.y) <= 1_000_000;
}

export interface WorkflowGraphPalette {
  readonly nodeSurface: string;
  readonly nodeBorder: string;
  readonly nodeText: string;
  readonly entrySurface: string;
  readonly entryBorder: string;
  readonly terminalSurface: string;
  readonly terminalBorder: string;
  readonly laneSurface: string;
  readonly laneAlternateSurface: string;
  readonly laneBorder: string;
  readonly laneHeaderSurface: string;
  readonly laneHeaderBorder: string;
  readonly laneHeaderText: string;
  readonly stageLaneSurface: string;
  readonly attentionLaneSurface: string;
  readonly decisionBandSurface: string;
  readonly decisionStrongSurface: string;
  readonly decisionGatewaySurface: string;
  readonly decisionSummarySurface: string;
  readonly decisionOutcomeSurface: string;
  readonly decisionFallbackSurface: string;
  readonly decisionBorder: string;
  readonly decisionStrongBorder: string;
  readonly decisionText: string;
  readonly decisionFallbackBorder: string;
  readonly decisionFallbackText: string;
  readonly decisionGuidanceText: string;
  readonly decisionAssociation: string;
  readonly decisionFlow: string;
  readonly decisionFlowText: string;
  readonly invalidSurface: string;
  readonly invalidBorder: string;
  readonly invalidText: string;
  readonly selectionBorder: string;
  readonly selectionOverlay: string;
  readonly edgeText: string;
  readonly edgeLabelSurface: string;
  readonly route: string;
  readonly stageRoute: string;
}

const WORKFLOW_GRAPH_PALETTE_TOKENS: Readonly<Record<keyof WorkflowGraphPalette, string>> = {
  nodeSurface: "--workflow-graph-node-surface-color",
  nodeBorder: "--workflow-graph-node-border-color",
  nodeText: "--workflow-graph-node-text-color",
  entrySurface: "--workflow-graph-entry-surface-color",
  entryBorder: "--workflow-graph-entry-border-color",
  terminalSurface: "--workflow-graph-terminal-surface-color",
  terminalBorder: "--workflow-graph-terminal-border-color",
  laneSurface: "--workflow-graph-lane-surface-color",
  laneAlternateSurface: "--workflow-graph-lane-alternate-surface-color",
  laneBorder: "--workflow-graph-lane-border-color",
  laneHeaderSurface: "--workflow-graph-lane-header-surface-color",
  laneHeaderBorder: "--workflow-graph-lane-header-border-color",
  laneHeaderText: "--workflow-graph-lane-header-text-color",
  stageLaneSurface: "--workflow-graph-stage-lane-surface-color",
  attentionLaneSurface: "--workflow-graph-attention-lane-surface-color",
  decisionBandSurface: "--workflow-graph-decision-band-surface-color",
  decisionStrongSurface: "--workflow-graph-decision-strong-surface-color",
  decisionGatewaySurface: "--workflow-graph-decision-gateway-surface-color",
  decisionSummarySurface: "--workflow-graph-decision-summary-surface-color",
  decisionOutcomeSurface: "--workflow-graph-decision-outcome-surface-color",
  decisionFallbackSurface: "--workflow-graph-decision-fallback-surface-color",
  decisionBorder: "--workflow-graph-decision-border-color",
  decisionStrongBorder: "--workflow-graph-decision-strong-border-color",
  decisionText: "--workflow-graph-decision-text-color",
  decisionFallbackBorder: "--workflow-graph-decision-fallback-border-color",
  decisionFallbackText: "--workflow-graph-decision-fallback-text-color",
  decisionGuidanceText: "--workflow-graph-decision-guidance-text-color",
  decisionAssociation: "--workflow-graph-decision-association-color",
  decisionFlow: "--workflow-graph-decision-flow-color",
  decisionFlowText: "--workflow-graph-decision-flow-text-color",
  invalidSurface: "--workflow-graph-invalid-surface-color",
  invalidBorder: "--workflow-graph-invalid-border-color",
  invalidText: "--workflow-graph-invalid-text-color",
  selectionBorder: "--workflow-graph-selection-border-color",
  selectionOverlay: "--workflow-graph-selection-overlay-color",
  edgeText: "--workflow-graph-edge-text-color",
  edgeLabelSurface: "--workflow-graph-edge-label-surface-color",
  route: "--workflow-graph-route-color",
  stageRoute: "--workflow-graph-stage-route-color",
};

export const WORKFLOW_GRAPH_LIGHT_PALETTE: WorkflowGraphPalette = {
  nodeSurface: "#f8fafc",
  nodeBorder: "#6f91ab",
  nodeText: "#152532",
  entrySurface: "#e8f5ec",
  entryBorder: "#3d7a4d",
  terminalSurface: "#edf1f4",
  terminalBorder: "#526473",
  laneSurface: "#fbfcfd",
  laneAlternateSurface: "#f4f7f9",
  laneBorder: "#71899a",
  laneHeaderSurface: "#eaf0f4",
  laneHeaderBorder: "#71899a",
  laneHeaderText: "#274357",
  stageLaneSurface: "#eef5f9",
  attentionLaneSurface: "#fff8ef",
  decisionBandSurface: "#fffaf0",
  decisionStrongSurface: "#fff3cf",
  decisionGatewaySurface: "#fff3c4",
  decisionSummarySurface: "#fff7dd",
  decisionOutcomeSurface: "#fffaf0",
  decisionFallbackSurface: "#f5f1e9",
  decisionBorder: "#b7791f",
  decisionStrongBorder: "#9a6700",
  decisionText: "#5f4100",
  decisionFallbackBorder: "#80652d",
  decisionFallbackText: "#493f35",
  decisionGuidanceText: "#6b5a33",
  decisionAssociation: "#c08a28",
  decisionFlow: "#b7791f",
  decisionFlowText: "#6c4a08",
  invalidSurface: "#fff1f0",
  invalidBorder: "#b42318",
  invalidText: "#6f241b",
  selectionBorder: "#174a68",
  selectionOverlay: "#27628a",
  edgeText: "#4c6070",
  edgeLabelSurface: "#ffffff",
  route: "#27628a",
  stageRoute: "#1f608b",
};

export function resolveWorkflowGraphPalette(container: HTMLElement): WorkflowGraphPalette {
  const view = container.ownerDocument.defaultView;
  const computedStyle = view?.getComputedStyle(container);
  const palette = { ...WORKFLOW_GRAPH_LIGHT_PALETTE };
  // Theme tokens can resolve to color-mix()/color(), which Cytoscape does not parse.
  // Let the browser convert these to sRGB before passing them to the canvas renderer.
  let colorContext: CanvasRenderingContext2D | null | undefined;

  for (const key of Object.keys(WORKFLOW_GRAPH_PALETTE_TOKENS) as Array<keyof WorkflowGraphPalette>) {
    const resolved = computedStyle
      ?.getPropertyValue(WORKFLOW_GRAPH_PALETTE_TOKENS[key])
      .trim();
    if (resolved) {
      if (/^(color-mix|color|oklch|oklab|lab|lch)\(/i.test(resolved)) {
        if (colorContext === undefined) {
          const canvas = container.ownerDocument.createElement("canvas");
          canvas.width = canvas.height = 1;
          colorContext = canvas.getContext("2d", { willReadFrequently: true });
        }
        if (colorContext) {
          colorContext.clearRect(0, 0, 1, 1);
          colorContext.fillStyle = resolved;
          colorContext.fillRect(0, 0, 1, 1);
          const [red, green, blue, alpha] = colorContext.getImageData(0, 0, 1, 1).data;
          palette[key] = `rgba(${red}, ${green}, ${blue}, ${alpha / 255})`;
          continue;
        }
      }
      palette[key] = resolved;
    }
  }

  return palette;
}

const WHOLE_GRAPH_FIT_PADDING = 36;
const OVERVIEW_GRAPH_FIT_PADDING = 18;
const OVERVIEW_STAGE_SLOT_WIDTH = 404;
const OVERVIEW_STAGE_SLOT_HEIGHT = 60;
const OVERVIEW_STAGE_WIDTH = 190;
const OVERVIEW_STAGE_HEIGHT = 56;
const OVERVIEW_SUMMARY_GAP = 18;
const OVERVIEW_SUMMARY_WIDTH = 180;
const OVERVIEW_SUMMARY_HEIGHT = 44;
const ROUTED_GRAPH_FIT_PADDING = 14;
const ROUTED_NODE_HEIGHT = 46;
const ROUTED_STAGE_LANE_MIN_WIDTH = 238;
const ROUTED_STAGE_LANE_PADDING = 24;
const ROUTED_DECISION_LANE_WIDTH = 360;
const ROUTED_LANE_HEADER_HEIGHT = 56;
const ROUTED_LANE_CONTENT_PADDING = 18;
const ROUTED_WORKFLOW_BAND_HEADER_HEIGHT = 36;
const ROUTED_WORKFLOW_GATE_HORIZONTAL_PADDING = 12;
const ROUTED_DECISION_UNIT_WIDTH = 344;
const ROUTED_DECISION_UNIT_HEIGHT = 158;
const ROUTED_DECISION_UNIT_GAP = 12;
const ROUTED_DECISION_DIAMOND_WIDTH = 104;
const ROUTED_DECISION_DIAMOND_HEIGHT = 76;
const ROUTED_DECISION_OUTCOME_WIDTH = 154;
const ROUTED_DECISION_OUTCOME_HEIGHT = 44;
const ROUTED_DECISION_OUTCOME_VERTICAL_GAP = 6;
const ROUTED_DECISION_ELEMENT_GAP = 74;
const ROUTED_DECISION_GUIDANCE_GAP = 8;
const ROUTED_DECISION_GUIDANCE_HEIGHT = 48;
const ROUTED_DECISION_BODY_HEIGHT = ROUTED_DECISION_OUTCOME_HEIGHT * 2
  + ROUTED_DECISION_OUTCOME_VERTICAL_GAP;
const ROUTED_MAX_CANVAS_LINE_LENGTH = 48;
const NODE_LABEL_SEGMENTER = new Intl.Segmenter(undefined, { granularity: "grapheme" });
const FALLBACK_STAGE_LANE_ID = "responsibility-lane:workflow-stages";
const FALLBACK_DECISION_LANE_ID = "responsibility-lane:decision-guidance";

interface WorkflowGraphResponsibilityLaneGeometry {
  readonly id: string;
  readonly label: string;
  readonly kind: string;
  readonly description: string;
  readonly x: number;
  readonly width: number;
}

interface WorkflowGraphRoutedGeometry {
  readonly lanes: readonly WorkflowGraphResponsibilityLaneGeometry[];
  readonly laneById: ReadonlyMap<string, WorkflowGraphResponsibilityLaneGeometry>;
  readonly stageLane: WorkflowGraphResponsibilityLaneGeometry;
  readonly stageContentOffsetX: number;
  readonly contentOffsetY: number;
  readonly laneBodyTop: number;
  readonly laneBodyHeight: number;
  readonly workflowBandHeight: number;
  readonly totalWidth: number;
  readonly totalHeight: number;
}

interface WorkflowGraphOverviewGeometry {
  readonly layoutMinX: number;
  readonly layoutMinY: number;
  readonly contentOffsetX: number;
  readonly contentOffsetY: number;
  readonly totalWidth: number;
  readonly totalHeight: number;
}

function routedSemanticNodeHeight(node: WorkflowReadOnlyGraphNode): number {
  return Math.min(node.height, ROUTED_NODE_HEIGHT);
}

function routedNodeSlotHeight(node: WorkflowReadOnlyGraphNode): number {
  const gatesByLane = new Map<string, number>();
  for (const gate of node.decisionGates ?? []) {
    const laneId = gate.responsibilityLaneId || FALLBACK_DECISION_LANE_ID;
    gatesByLane.set(laneId, (gatesByLane.get(laneId) ?? 0) + 1);
  }
  const largestLaneStack = Math.max(0, ...gatesByLane.values());
  const gateStackHeight = largestLaneStack === 0
    ? 0
    : largestLaneStack * ROUTED_DECISION_UNIT_HEIGHT
      + (largestLaneStack - 1) * ROUTED_DECISION_UNIT_GAP;
  return Math.max(routedSemanticNodeHeight(node), gateStackHeight);
}

export function reciprocalWorkflowGraphEdgeIds(
  edges: readonly WorkflowReadOnlyGraphEdge[],
): ReadonlySet<string> {
  return new Set(edges
    .filter((edge) => edge.source !== edge.target && edges.some((candidate) =>
      candidate.id !== edge.id
      && candidate.source === edge.target
      && candidate.target === edge.source))
    .map((edge) => edge.id));
}

/** Separate labels for distinct automatic outcomes that lead to the same stage. */
export function automatedParallelOutcomeOffsets(
  graph: Pick<WorkflowReadOnlyGraph, "nodes" | "edges">,
): ReadonlyMap<string, number> {
  const automaticNodes = new Set(graph.nodes
    .filter((node) => node.kind === "automated-step")
    .map((node) => node.id));
  const groups = new Map<string, WorkflowReadOnlyGraphEdge[]>();
  for (const edge of graph.edges) {
    if (!automaticNodes.has(edge.source) || !edge.resultLabel || edge.source === edge.target) continue;
    const key = JSON.stringify([edge.source, edge.target]);
    const group = groups.get(key) ?? [];
    group.push(edge);
    groups.set(key, group);
  }
  const offsets = new Map<string, number>();
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    const ordered = [...group].sort((left, right) => left.id < right.id ? -1 : left.id > right.id ? 1 : 0);
    ordered.forEach((edge, index) => offsets.set(edge.id, (index - (ordered.length - 1) / 2) * 240));
  }
  return offsets;
}

export function workflowGraphNodeVisualLabel(
  node: WorkflowReadOnlyGraphNode,
  profile: WorkflowGraphLayoutProfile,
): string {
  const label = profile !== "compact"
    && node.kind === "human-step"
    && (node.processOrder ?? null) !== null
    ? `${node.processOrder} · ${node.label}`
    : node.label;
  // Three 12px lines fit the smallest 46px stage. Keep the full name in data.label.
  const normalized = label.replace(/\s+/g, " ").trim();
  const graphemes = Array.from(NODE_LABEL_SEGMENTER.segment(normalized));
  const bounded = normalized.length <= 32 ? normalized
    : `${graphemes.filter(({ index, segment }) => index + segment.length <= 31)
      .map(({ segment }) => segment).join("").trimEnd()}…`;
  // Cytoscape's anywhere wrapping splits UTF-16 pairs. Supply safe opportunities
  // at grapheme boundaries when a label may exceed the existing 132px line.
  return bounded.length > 22 || bounded.split(" ").some(word => word.length > 11)
    ? Array.from(NODE_LABEL_SEGMENTER.segment(bounded), ({ segment }) => segment).join("\u200b")
    : bounded;
}

export function workflowDecisionQuestionVisualLabel(
  gate: WorkflowReadOnlyGraphDecisionGate,
): string {
  return boundedCanvasText(gate.umlQuestion || gate.title, ROUTED_MAX_CANVAS_LINE_LENGTH);
}

export function workflowDecisionMatchedVisualLabel(
  gate: WorkflowReadOnlyGraphDecisionGate,
): string {
  if (gate.isInvalid) {
    return "Needs attention\nRepair saved requirement";
  }
  return [
    boundedCanvasText(gate.matchedOutcomeLabel || "Required result", 24),
    boundedCanvasText(gate.matchedEffect || gate.effect, 38),
  ].filter(Boolean).join("\n");
}

export function workflowDecisionFallbackVisualLabel(
  gate: WorkflowReadOnlyGraphDecisionGate,
): string {
  const fallback = /status change blocked/i.test(gate.blockedFallback)
    ? "Status change blocked"
    : boundedCanvasText(gate.blockedFallback || "Status change blocked", 38);
  return [
    "Otherwise",
    fallback,
  ].join("\n");
}

export function workflowDecisionGuidanceVisualLabel(
  gate: WorkflowReadOnlyGraphDecisionGate,
): string {
  const roles = gate.suggestedRoleCodes ?? [];
  const visibleRoles = roles.slice(0, 1).map(humanizeCode);
  const roleSummary = visibleRoles.length === 0
    ? ""
    : `Roles: ${visibleRoles.join(", ")}${roles.length > visibleRoles.length ? ` +${roles.length - visibleRoles.length}` : ""}`;
  const notificationSummary = gate.notificationCount > 0
    ? `${gate.notificationCount} notification${gate.notificationCount === 1 ? "" : "s"}`
    : "";
  return [
    boundedCanvasText(gate.scopeLabel, 34),
    boundedCanvasText(roleSummary, 34),
    notificationSummary,
    gate.isLegacyOnly ? "Legacy" : "",
  ].filter(Boolean).join("\n");
}

/**
 * One approachable label for all decision requirements at a stage or workflow
 * scope. The overview deliberately avoids reproducing the detailed gate model.
 */
export function workflowDecisionSummaryVisualLabel(
  gates: readonly WorkflowReadOnlyGraphDecisionGate[],
): string {
  if (gates.length === 0) {
    return "";
  }
  if (gates.some((gate) => gate.isInvalid)) {
    return "Needs attention";
  }

  const firstTitle = conciseDecisionTitle(gates[0]);
  if (gates.length === 1) {
    return boundedCanvasText(firstTitle, 38);
  }

  const firstKind = firstTitle
    .replace(/\s+(?:is\s+)?required[.!]?$/i, "")
    .trim() || "Decision";
  return `${boundedCanvasText(firstKind, 24)} + ${gates.length - 1} more`;
}

function conciseDecisionTitle(gate: WorkflowReadOnlyGraphDecisionGate): string {
  const fallbackKind = humanizeCode(gate.decisionKindCode ?? "Decision");
  const title = gate.title.trim() || `${fallbackKind} required`;
  return title
    .replace(/\s+is\s+required([.!]?)$/i, " required$1")
    .replace(/\s+/g, " ")
    .trim();
}

function boundedCanvasText(value: string | null | undefined, maximumLength: number): string {
  const normalized = (value ?? "").replace(/\s+/g, " ").trim();
  if (normalized.length <= maximumLength) {
    return normalized;
  }
  return `${normalized.slice(0, maximumLength - 1).trimEnd()}…`;
}

function humanizeCode(value: string): string {
  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (character) => character.toLocaleUpperCase());
}

function isAuthorableGraphNode(
  node: WorkflowReadOnlyGraphNode,
  nodes: readonly WorkflowReadOnlyGraphNode[],
): boolean {
  return !node.isInvalid
    && node.sourceId.trim().length > 0
    && nodes.filter((candidate) => candidate.sourceId === node.sourceId).length === 1;
}

function hasLayoutPosition(node: ElkNode): node is WorkflowGraphLayoutNode {
  return typeof node.x === "number"
    && typeof node.y === "number"
    && typeof node.width === "number"
    && typeof node.height === "number";
}

function fallbackStageLane(): WorkflowReadOnlyGraphResponsibilityLane {
  return {
    id: FALLBACK_STAGE_LANE_ID,
    kind: "stage",
    scope: "workflow-stages",
    label: "Workflow stages",
    description: "The saved document does not record a work-step owner.",
    queueGuidanceCodes: [],
    suggestedRoleCodes: [],
    gateCount: 0,
  };
}

function fallbackDecisionLane(id: string): WorkflowReadOnlyGraphResponsibilityLane {
  return {
    id,
    kind: "unassigned-guidance",
    scope: "step-decisions",
    label: "Decision guidance",
    description: "No single intended decision queue or role can be shown.",
    queueGuidanceCodes: [],
    suggestedRoleCodes: [],
    gateCount: 0,
  };
}

function laneSubtitle(kind: string): string {
  switch (kind) {
    case "stage":
      return "Step owner not recorded";
    case "queue-guidance":
      return "Intended decision queue";
    case "role-guidance":
      return "Suggested decision roles";
    case "mixed-guidance":
      return "Mixed responsibility guidance";
    case "needs-attention":
      return "Saved guidance needs attention";
    default:
      return "Decision guidance not recorded";
  }
}

@Injectable()
export class WorkflowReadOnlyGraphVisualizationService {
  private elk?: {
    layout: (graph: ElkNode) => Promise<ElkNode>;
  };
  private readonly scheduledAnimationFrames = new Set<number>();
  private readonly fitPaddingByViewport = new WeakMap<WorkflowGraphViewport, number>();
  private readonly routedViewports = new WeakSet<WorkflowGraphViewport>();

  async layout(
    graph: WorkflowReadOnlyGraph,
    profile: WorkflowGraphLayoutProfile = "compact",
  ): Promise<WorkflowGraphLayout> {
    if(profile==='overview')return workflowMapLayout(graph);
    const elk = await this.getElk();
    const layoutOptions: Record<string, string> = profile === "compact"
      ? {
          "elk.algorithm": "layered",
          "elk.aspectRatio": "1.45",
          "elk.direction": "RIGHT",
          "elk.edgeRouting": "ORTHOGONAL",
          "elk.spacing.nodeNode": "58",
          "elk.layered.spacing.nodeNodeBetweenLayers": "90",
          "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
          "elk.layered.wrapping.strategy": "MULTI_EDGE",
        }
      : {
            "elk.algorithm": "layered",
            "elk.direction": "DOWN",
            "elk.edgeRouting": "ORTHOGONAL",
            "elk.spacing.nodeNode": "32",
            "elk.layered.spacing.nodeNodeBetweenLayers": "10",
            "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
            "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
            "elk.layered.wrapping.strategy": "OFF",
          };

    return elk.layout({
      id: "workflow-read-only-graph",
      layoutOptions,
      children: graph.nodes.map((node) => ({
        id: node.id,
        width: node.width,
        height: profile === "routed"
          ? routedNodeSlotHeight(node)
          : node.height,
      })),
      edges: graph.edges.map((edge) => ({
        id: edge.id,
        sources: [edge.source],
        targets: [edge.target],
      })),
    });
  }

  async render(
    container: HTMLElement,
    graph: WorkflowReadOnlyGraph,
    layout: WorkflowGraphLayout,
    callbacks: WorkflowGraphSelectionCallbacks,
    profile: WorkflowGraphLayoutProfile = "compact",
  ): Promise<WorkflowGraphViewport> {
    const { default: cytoscape } = await import("cytoscape");
    const layoutNodes = new Map<string, WorkflowGraphLayoutNode>();
    const reciprocalEdgeIds = reciprocalWorkflowGraphEdgeIds(graph.edges);
    const automatedParallelOffsets = automatedParallelOutcomeOffsets(graph);
    layout.children?.forEach((node) => {
      if (hasLayoutPosition(node)) {
        layoutNodes.set(node.id, node);
      }
    });
    const routedGeometry = profile === "routed"
      ? this.buildRoutedGeometry(graph, layoutNodes)
      : null;
    const overviewGeometry = profile === "overview"
      ? this.buildOverviewGeometry(graph, layoutNodes)
      : null;

    const semanticElements: ElementDefinition[] = [
      ...graph.nodes.map((node, index) => {
        const layoutNode = layoutNodes.get(node.id);
        const routedPosition = routedGeometry
          ? this.routedNodePosition(node, layoutNode, index, routedGeometry)
          : null;
        const overviewPosition = overviewGeometry
          ? this.overviewNodePosition(node, layoutNode, index, overviewGeometry)
          : null;
        return {
          group: "nodes" as const,
          data: {
            id: node.id,
            label: node.label,
            visualLabel: workflowGraphNodeVisualLabel(node, profile),
            kind: node.kind,
            width: node.width,
            height: profile === "routed"
              ? routedSemanticNodeHeight(node)
              : profile === "overview"
                ? Math.min(node.height, OVERVIEW_STAGE_HEIGHT)
                : node.height,
          },
          position: routedPosition ?? overviewPosition ?? {
            x: layoutNode
              ? layoutNode.x + (profile === "routed" ? node.width : layoutNode.width) / 2
              : 120 + index * 190,
            y: layoutNode ? layoutNode.y + layoutNode.height / 2 : 120,
          },
          classes: node.isInvalid ? `${node.kind} invalid` : node.kind,
          grabbable: !!callbacks.move && profile==='overview' && node.sourceIndex!==null,
        };
      }),
      ...graph.edges.map((edge) => ({
        group: "edges" as const,
        data: {
          id: edge.id,
          source: edge.source,
          target: edge.target,
          label: edge.label,
          resultLabel: edge.resultLabel,
          automatedParallelOffset: (automatedParallelOffsets.get(edge.id) ?? 0) * (profile==='overview'?0.22:1),
          returnOffset: -360 - 280 * graph.edges.filter(e=>e.pathKindCode==='return'&&e.source===edge.source&&e.target===edge.target).sort((a,b)=>a.id.localeCompare(b.id)).findIndex(e=>e.id===edge.id),
          kind: edge.kind,
        },
        classes: [
          edge.kind,
          edge.resultLabel ? "routed-outcome" : "",
          reciprocalEdgeIds.has(edge.id) ? "reciprocal" : "",
          automatedParallelOffsets.has(edge.id) ? "automated-parallel-outcome" : "",
          profile === "routed" || profile === "overview" ? "stage-path" : "",
          profile === "overview" ? "overview-stage-path" : "",
          edge.pathKindCode==='return' ? 'workflow-return' : '',
          profile==='overview' && graph.nodes.some(n=>n.id===edge.target&&n.kind==='terminal') ? 'workflow-terminal-branch' : '',
          profile==='overview' && edge.pathKindCode!=='return' && layoutNodes.get(edge.source)?.x===layoutNodes.get(edge.target)?.x
            && (layoutNodes.get(edge.target)?.y??0)-(layoutNodes.get(edge.source)?.y??0)>80 ? 'workflow-bypass' : '',
        ].filter(Boolean).join(" "),
      })),
    ];
    const routedVisualElements = routedGeometry
      ? this.routedVisualElements(graph, layoutNodes, routedGeometry)
      : [];
    const overviewVisualElements = overviewGeometry
      ? this.overviewVisualElements(graph, layoutNodes, overviewGeometry)
      : [];
    const elements: ElementDefinition[] = [
      ...routedVisualElements.filter((element) =>
        element.classes?.toString().includes("swimlane")),
      ...semanticElements,
      ...overviewVisualElements,
      ...routedVisualElements.filter((element) =>
        !element.classes?.toString().includes("swimlane")),
    ];
    // Summary badges belong to their stage, so restored and dragged positions move them together.
    for(const node of semanticElements.filter(e=>e.group==='nodes')){
      const saved=callbacks.positions?.[String(node.data.id)];if(!saved||!node.position)continue;
      const delta={x:saved.x-node.position.x,y:saved.y-node.position.y};node.position={...saved};
      for(const badge of overviewVisualElements)if(badge.data['ownerRendererId']===node.data.id&&badge.position)
        badge.position={x:badge.position.x+delta.x,y:badge.position.y+delta.y};
    }

    const viewportContainer = document.createElement("div");
    viewportContainer.className = "workflow-read-only-graph-viewport";
    this.configureViewportContainer(
      viewportContainer,
      routedGeometry ?? overviewGeometry,
      profile === "overview" ? OVERVIEW_GRAPH_FIT_PADDING : ROUTED_GRAPH_FIT_PADDING,
    );
    container.appendChild(viewportContainer);

    let nextViewport: WorkflowGraphViewport | undefined;
    try {
      nextViewport = cytoscape({
        container: viewportContainer,
        elements,
        layout: { name: "preset" },
        autoungrabify: !callbacks.move || profile!=='overview',
        boxSelectionEnabled: false,
        maxZoom: 2.2,
        minZoom: 0.25,
        style: this.styles(resolveWorkflowGraphPalette(container)),
      });
      this.fitPaddingByViewport.set(
        nextViewport,
        profile === "routed"
          ? ROUTED_GRAPH_FIT_PADDING
          : profile === "overview"
            ? OVERVIEW_GRAPH_FIT_PADDING
            : WHOLE_GRAPH_FIT_PADDING,
      );
      if (profile === "routed" || profile === "overview") {
        this.routedViewports.add(nextViewport);
      }

      nextViewport.on("tap", "node, edge", (event) => {
        this.handleElementTap(event.target as unknown as WorkflowGraphTapTarget, callbacks);
      });
      if(callbacks.move && profile==='overview'){
        const previous=new Map<string,WorkflowMapPoint>();
        nextViewport.nodes().forEach(n=>{previous.set(n.id(),{...n.position()});});
        nextViewport.on('position','node',event=>{
          const node=event.target;if(!node.grabbable())return;
          const before=previous.get(node.id()),after={...node.position()};previous.set(node.id(),after);
          if(!before)return;
          nextViewport!.nodes().filter(b=>b.data('ownerRendererId')===node.id()).forEach(b=>{
            const point=b.position();b.unlock();b.position({x:point.x+after.x-before.x,y:point.y+after.y-before.y});b.lock();
          });
        });
        nextViewport.on('dragfree','node',event=>{if(event.target.grabbable())callbacks.move!(event.target.id(),{...event.target.position()});});
      }
      nextViewport.on("mouseover", "node.decision-action, node.decision-summary-action", () => {
        viewportContainer.style.cursor = "pointer";
      });
      nextViewport.on("mouseout", "node.decision-action, node.decision-summary-action", () => {
        viewportContainer.style.cursor = "";
      });
      nextViewport.on("tap", (event) => {
        if (event.target === nextViewport) {
          callbacks.clear();
        }
      });

      this.fit(nextViewport);
      return nextViewport;
    } catch (error: unknown) {
      nextViewport?.destroy();
      viewportContainer.remove();
      throw error;
    }
  }

  select(viewport: WorkflowGraphViewport | undefined, elementId: string | null): void {
    if (!viewport) {
      return;
    }

    viewport.elements().unselect();
    if (!elementId) {
      return;
    }

    const selectedElement = viewport.getElementById(elementId);
    if (selectedElement.length === 0) {
      return;
    }

    selectedElement.select();
  }

  moveNode(viewport:WorkflowGraphViewport|undefined,id:string,dx:number,dy:number):WorkflowMapPoint|null {
    const node=viewport?.getElementById(id);if(!node?.length||!node.isNode()||!node.grabbable())return null;
    const point=node.position();node.position({x:point.x+dx,y:point.y+dy});return {...node.position()};
  }

  zoomBy(viewport: WorkflowGraphViewport | undefined, multiplier: number): void {
    if (!viewport) {
      return;
    }

    const nextZoom = Math.min(
      viewport.maxZoom(),
      Math.max(viewport.minZoom(), viewport.zoom() * multiplier),
    );
    viewport.zoom({
      level: nextZoom,
      renderedPosition: {
        x: viewport.width() / 2,
        y: viewport.height() / 2,
      },
    });
  }

  resize(viewport: WorkflowGraphViewport | undefined): void {
    viewport?.resize();
  }

  captureCamera(viewport: WorkflowGraphViewport | undefined): WorkflowGraphCameraState | null {
    if (!viewport || viewport.destroyed()) return null;
    const zoom = viewport.zoom(), pan = viewport.pan();
    const state = { zoom, pan: { x: pan.x, y: pan.y } };
    return validWorkflowGraphCameraState(state) && zoom >= viewport.minZoom() && zoom <= viewport.maxZoom() ? state : null;
  }

  restoreCamera(viewport: WorkflowGraphViewport | undefined, state: WorkflowGraphCameraState): boolean {
    if (!viewport || viewport.destroyed() || !validWorkflowGraphCameraState(state)
      || state.zoom < viewport.minZoom() || state.zoom > viewport.maxZoom()) return false;
    // A second frame from an earlier automatic fit must not overwrite this explicit return.
    this.cancelScheduledFrames();
    viewport.resize();
    viewport.zoom(state.zoom);
    viewport.pan({ x: state.pan.x, y: state.pan.y });
    return true;
  }

  applyTheme(
    viewport: WorkflowGraphViewport | undefined,
    container: HTMLElement | undefined,
  ): void {
    if (!viewport || !container || viewport.destroyed()) {
      return;
    }

    viewport.style(this.styles(resolveWorkflowGraphPalette(container))).update();
  }

  fit(viewport: WorkflowGraphViewport | undefined): void {
    if (!viewport || viewport.nodes().length === 0) {
      return;
    }

    this.scheduleFit(
      viewport,
      viewport.elements(),
      this.fitPaddingByViewport.get(viewport) ?? WHOLE_GRAPH_FIT_PADDING,
    );
  }

  private scheduleFit(
    viewport: WorkflowGraphViewport,
    elements: CollectionReturnValue,
    padding: number,
  ): void {
    const fit = (): void => {
      if (viewport.destroyed()) {
        return;
      }

      viewport.resize();
      viewport.fit(elements, padding);
      if (this.routedViewports.has(viewport) && viewport.zoom() > 1) {
        viewport.zoom(1);
      }
      viewport.center(elements);
      if (this.routedViewports.has(viewport)) {
        const bounds = elements.renderedBoundingBox();
        viewport.panBy({ x: 0, y: padding - bounds.y1 });
      }
    };

    if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
      fit();
      return;
    }

    this.cancelScheduledFrames();
    this.scheduleAnimationFrame(() => {
      fit();
      this.scheduleAnimationFrame(fit);
    });
  }

  destroy(viewport: WorkflowGraphViewport | undefined): void {
    if (viewport) {
      this.fitPaddingByViewport.delete(viewport);
      this.routedViewports.delete(viewport);
    }
    const container = viewport?.container();
    viewport?.destroy();
    container?.remove();
  }

  cancelPendingFrames(): void {
    this.cancelScheduledFrames();
  }

  private scheduleAnimationFrame(callback: () => void): void {
    const frameId = window.requestAnimationFrame(() => {
      this.scheduledAnimationFrames.delete(frameId);
      callback();
    });
    this.scheduledAnimationFrames.add(frameId);
  }

  private cancelScheduledFrames(): void {
    if (typeof window === "undefined" || typeof window.cancelAnimationFrame !== "function") {
      this.scheduledAnimationFrames.clear();
      return;
    }

    this.scheduledAnimationFrames.forEach((frameId) => window.cancelAnimationFrame(frameId));
    this.scheduledAnimationFrames.clear();
  }

  private async getElk(): Promise<{
    layout: (graph: ElkNode) => Promise<ElkNode>;
  }> {
    if (!this.elk) {
      const { default: ElkConstructor } = await import("elkjs/lib/elk.bundled");
      this.elk = new ElkConstructor({ algorithms: ["layered"] });
    }

    return this.elk;
  }

  private buildOverviewGeometry(
    graph: WorkflowReadOnlyGraph,
    layoutNodes: ReadonlyMap<string, WorkflowGraphLayoutNode>,
  ): WorkflowGraphOverviewGeometry {
    const positionedNodes = [...layoutNodes.values()];
    const layoutMinX = positionedNodes.length > 0
      ? Math.min(...positionedNodes.map((node) => node.x))
      : 0;
    const layoutMaxX = positionedNodes.length > 0
      ? Math.max(...positionedNodes.map((node) => node.x + node.width))
      : OVERVIEW_STAGE_SLOT_WIDTH;
    const layoutMinY = positionedNodes.length > 0
      ? Math.min(...positionedNodes.map((node) => node.y))
      : 0;
    const layoutMaxY = positionedNodes.length > 0
      ? Math.max(...positionedNodes.map((node) => node.y + node.height))
      : Math.max(OVERVIEW_STAGE_SLOT_HEIGHT, graph.nodes.length * OVERVIEW_STAGE_SLOT_HEIGHT);
    return {
      layoutMinX,
      layoutMinY,
      contentOffsetX: -layoutMinX,
      contentOffsetY: -layoutMinY,
      totalWidth: Math.max(OVERVIEW_STAGE_SLOT_WIDTH, layoutMaxX - layoutMinX),
      totalHeight: Math.max(
        OVERVIEW_STAGE_SLOT_HEIGHT,
        layoutMaxY - layoutMinY,
      ),
    };
  }

  private overviewNodePosition(
    node: WorkflowReadOnlyGraphNode,
    layoutNode: WorkflowGraphLayoutNode | undefined,
    nodeIndex: number,
    geometry: WorkflowGraphOverviewGeometry,
  ): { readonly x: number; readonly y: number } {
    const slotLeft = layoutNode
      ? geometry.contentOffsetX + layoutNode.x
      : 0;
    return {
      x: slotLeft + node.width / 2,
      y: layoutNode
        ? geometry.contentOffsetY + layoutNode.y + layoutNode.height / 2
        : geometry.contentOffsetY
          + nodeIndex * (OVERVIEW_STAGE_SLOT_HEIGHT + 16)
          + OVERVIEW_STAGE_SLOT_HEIGHT / 2,
    };
  }

  private overviewVisualElements(
    graph: WorkflowReadOnlyGraph,
    layoutNodes: ReadonlyMap<string, WorkflowGraphLayoutNode>,
    geometry: WorkflowGraphOverviewGeometry,
  ): ElementDefinition[] {
    const elements = graph.nodes.flatMap((node, nodeIndex) => {
      const gates = node.kind === "human-step" ? node.decisionGates ?? [] : [];
      if (gates.length === 0) {
        return [];
      }
      const layoutNode = layoutNodes.get(node.id);
      const ownerAuthorable = this.isAuthorableDecisionScopeOwner(node, graph.nodes);
      return [this.overviewDecisionSummaryElement(
        gates,
        {
          ownerSourceNodeId: node.sourceId,
          ownerSourceNodeIndex: node.sourceIndex,
        },
        node.id,
        ownerAuthorable,
        {
          x: layoutNode
            ? geometry.contentOffsetX
              + layoutNode.x
              + OVERVIEW_STAGE_WIDTH
              + OVERVIEW_SUMMARY_GAP
              + OVERVIEW_SUMMARY_WIDTH / 2
            : OVERVIEW_STAGE_WIDTH
              + OVERVIEW_SUMMARY_GAP
              + OVERVIEW_SUMMARY_WIDTH / 2,
          y: layoutNode
            ? geometry.contentOffsetY + layoutNode.y + layoutNode.height / 2
            : geometry.contentOffsetY
              + nodeIndex * (OVERVIEW_STAGE_SLOT_HEIGHT + 16)
              + OVERVIEW_STAGE_SLOT_HEIGHT / 2,
        },
        `step__${encodeURIComponent(node.id)}`,
      )];
    });

    return elements;
  }

  private overviewDecisionSummaryElement(
    gates: readonly WorkflowReadOnlyGraphDecisionGate[],
    request: WorkflowReadOnlyGraphDecisionScopeRequest,
    ownerRendererId: string | null,
    ownerAuthorable: boolean,
    position: { readonly x: number; readonly y: number },
    idScope: string,
  ): ElementDefinition {
    const needsRepair = gates.some((gate) => gate.isInvalid) || !ownerAuthorable;
    return {
      group: "nodes",
      data: {
        id: `__workflow-decision-summary__${idScope}`,
        visualLabel: workflowDecisionSummaryVisualLabel(gates),
        width: OVERVIEW_SUMMARY_WIDTH,
        height: OVERVIEW_SUMMARY_HEIGHT,
        ownerRendererId,
        ownerAuthorable,
        decisionScopeRequest: request,
        decisionScopeNeedsRepair: needsRepair,
        decisionRuleCount: gates.length,
      },
      position,
      classes: [
        "decision-summary-action",
        request.ownerSourceNodeId === null ? "decision-summary-action--workflow" : "",
        gates.some((gate) => gate.isInvalid) ? "decision-summary-action--invalid" : "",
        !ownerAuthorable ? "decision-summary-action--repair" : "",
      ].filter(Boolean).join(" "),
      selectable: false,
      grabbable: false,
      locked: true,
    };
  }

  private isAuthorableDecisionScopeOwner(
    node: WorkflowReadOnlyGraphNode,
    nodes: readonly WorkflowReadOnlyGraphNode[],
  ): boolean {
    return typeof node.sourceIndex === "number" && isAuthorableGraphNode(node, nodes);
  }

  private buildRoutedGeometry(
    graph: WorkflowReadOnlyGraph,
    layoutNodes: ReadonlyMap<string, WorkflowGraphLayoutNode>,
  ): WorkflowGraphRoutedGeometry {
    const configuredLanes = graph.responsibilityLanes ?? [];
    const stageLaneModel = configuredLanes.find((lane) => lane.kind === "stage")
      ?? fallbackStageLane();
    const referencedLaneIds = new Set(graph.nodes.flatMap((node) =>
      (node.decisionGates ?? []).map((gate) =>
        gate.responsibilityLaneId || FALLBACK_DECISION_LANE_ID)));
    const configuredDecisionLanes = configuredLanes.filter((lane) =>
      lane.scope === "step-decisions"
      && lane.kind !== "stage"
      && referencedLaneIds.has(lane.id));
    const configuredDecisionLaneIds = new Set(configuredDecisionLanes.map((lane) => lane.id));
    const missingDecisionLanes = [...referencedLaneIds]
      .filter((laneId) => !configuredDecisionLaneIds.has(laneId))
      .sort()
      .map(fallbackDecisionLane);
    const decisionLaneModels = [...configuredDecisionLanes, ...missingDecisionLanes];

    const positionedNodes = [...layoutNodes.values()];
    const layoutMinX = positionedNodes.length > 0
      ? Math.min(...positionedNodes.map((node) => node.x))
      : 0;
    const layoutMaxX = positionedNodes.length > 0
      ? Math.max(...positionedNodes.map((node) => node.x + node.width))
      : ROUTED_STAGE_LANE_MIN_WIDTH;
    const layoutMinY = positionedNodes.length > 0
      ? Math.min(...positionedNodes.map((node) => node.y))
      : 0;
    const layoutMaxY = positionedNodes.length > 0
      ? Math.max(...positionedNodes.map((node) => node.y + node.height))
      : ROUTED_NODE_HEIGHT;
    const stageLaneWidth = Math.max(
      ROUTED_STAGE_LANE_MIN_WIDTH,
      layoutMaxX - layoutMinX + ROUTED_STAGE_LANE_PADDING * 2,
    );

    let laneLeft = 0;
    const lanes: WorkflowGraphResponsibilityLaneGeometry[] = [
      stageLaneModel,
      ...decisionLaneModels,
    ].map((lane, index) => {
      const width = index === 0 ? stageLaneWidth : ROUTED_DECISION_LANE_WIDTH;
      const geometry = {
        id: lane.id,
        label: lane.label,
        kind: lane.kind,
        description: lane.description,
        x: laneLeft + width / 2,
        width,
      };
      laneLeft += width;
      return geometry;
    });
    const totalWidth = Math.max(laneLeft, ROUTED_STAGE_LANE_MIN_WIDTH);
    const workflowBandHeight = this.workflowBandHeight(
      graph.workflowDecisionGates?.length ?? 0,
      totalWidth,
    );
    const laneBodyTop = workflowBandHeight + ROUTED_LANE_HEADER_HEIGHT;
    const laneBodyHeight = Math.max(
      ROUTED_NODE_HEIGHT + ROUTED_LANE_CONTENT_PADDING * 2,
      layoutMaxY - layoutMinY + ROUTED_LANE_CONTENT_PADDING * 2,
    );
    const laneById = new Map(lanes.map((lane) => [lane.id, lane]));

    return {
      lanes,
      laneById,
      stageLane: lanes[0],
      stageContentOffsetX: lanes[0].x - lanes[0].width / 2
        + ROUTED_STAGE_LANE_PADDING - layoutMinX,
      contentOffsetY: laneBodyTop + ROUTED_LANE_CONTENT_PADDING - layoutMinY,
      laneBodyTop,
      laneBodyHeight,
      workflowBandHeight,
      totalWidth,
      totalHeight: laneBodyTop + laneBodyHeight,
    };
  }

  private workflowBandHeight(gateCount: number, totalWidth: number): number {
    if (gateCount === 0) {
      return 0;
    }
    const gatesPerRow = Math.max(
      1,
      Math.floor(
        (totalWidth - ROUTED_WORKFLOW_GATE_HORIZONTAL_PADDING * 2)
        / (ROUTED_DECISION_UNIT_WIDTH + ROUTED_DECISION_UNIT_GAP),
      ),
    );
    const rowCount = Math.ceil(gateCount / gatesPerRow);
    return ROUTED_WORKFLOW_BAND_HEADER_HEIGHT
      + rowCount * ROUTED_DECISION_UNIT_HEIGHT
      + Math.max(0, rowCount - 1) * ROUTED_DECISION_UNIT_GAP
      + ROUTED_DECISION_UNIT_GAP;
  }

  private configureViewportContainer(
    viewportContainer: HTMLElement,
    geometry: Pick<WorkflowGraphRoutedGeometry, "totalWidth" | "totalHeight"> | null,
    fitPadding = ROUTED_GRAPH_FIT_PADDING,
  ): void {
    viewportContainer.style.position = "absolute";
    viewportContainer.style.inset = "0";
    viewportContainer.style.width = "100%";
    viewportContainer.style.height = "100%";
    if (!geometry) {
      return;
    }

    // Keep UML labels at their authored font size. The containing work surface owns
    // scrolling when the full lane geometry is wider or taller than its viewport.
    viewportContainer.style.right = "auto";
    viewportContainer.style.bottom = "auto";
    viewportContainer.style.minWidth = `${Math.ceil(
      geometry.totalWidth + fitPadding * 2,
    )}px`;
    viewportContainer.style.minHeight = `${Math.ceil(
      geometry.totalHeight + fitPadding * 2,
    )}px`;
  }

  private routedNodePosition(
    node: WorkflowReadOnlyGraphNode,
    layoutNode: WorkflowGraphLayoutNode | undefined,
    nodeIndex: number,
    geometry: WorkflowGraphRoutedGeometry,
  ): { readonly x: number; readonly y: number } {
    return {
      x: layoutNode
        ? geometry.stageContentOffsetX + layoutNode.x + node.width / 2
        : geometry.stageLane.x,
      y: layoutNode
        ? geometry.contentOffsetY + layoutNode.y + layoutNode.height / 2
        : geometry.contentOffsetY + nodeIndex * (ROUTED_NODE_HEIGHT + 24),
    };
  }

  private routedVisualElements(
    graph: WorkflowReadOnlyGraph,
    layoutNodes: ReadonlyMap<string, WorkflowGraphLayoutNode>,
    geometry: WorkflowGraphRoutedGeometry,
  ): ElementDefinition[] {
    return [
      ...this.responsibilityLaneElements(geometry),
      ...this.workflowDecisionBandElements(graph.workflowDecisionGates ?? [], geometry),
      ...graph.nodes.flatMap((node, nodeIndex) => this.stepDecisionElements(
        node,
        layoutNodes.get(node.id),
        nodeIndex,
        geometry,
        isAuthorableGraphNode(node, graph.nodes),
      )),
    ];
  }

  private responsibilityLaneElements(
    geometry: WorkflowGraphRoutedGeometry,
  ): ElementDefinition[] {
    return geometry.lanes.flatMap((lane, index): ElementDefinition[] => [
      {
        group: "nodes",
        data: {
          id: `__workflow-swimlane-body__${encodeURIComponent(lane.id)}`,
          visualLabel: "",
          width: lane.width,
          height: geometry.laneBodyHeight,
        },
        position: {
          x: lane.x,
          y: geometry.laneBodyTop + geometry.laneBodyHeight / 2,
        },
        classes: [
          "swimlane-body",
          `swimlane-body--${index % 2 === 0 ? "even" : "odd"}`,
          `swimlane--${lane.kind}`,
        ].join(" "),
        selectable: false,
        grabbable: false,
        locked: true,
      },
      {
        group: "nodes",
        data: {
          id: `__workflow-swimlane-header__${encodeURIComponent(lane.id)}`,
          visualLabel: `${lane.label}\n${laneSubtitle(lane.kind)}`,
          width: lane.width,
          height: ROUTED_LANE_HEADER_HEIGHT,
        },
        position: {
          x: lane.x,
          y: geometry.workflowBandHeight + ROUTED_LANE_HEADER_HEIGHT / 2,
        },
        classes: `swimlane-header swimlane--${lane.kind}`,
        selectable: false,
        grabbable: false,
        locked: true,
      },
    ]);
  }

  private workflowDecisionBandElements(
    gates: readonly WorkflowReadOnlyGraphDecisionGate[],
    geometry: WorkflowGraphRoutedGeometry,
  ): ElementDefinition[] {
    if (gates.length === 0) {
      return [];
    }
    const gatesPerRow = Math.max(
      1,
      Math.floor(
        (geometry.totalWidth - ROUTED_WORKFLOW_GATE_HORIZONTAL_PADDING * 2)
        / (ROUTED_DECISION_UNIT_WIDTH + ROUTED_DECISION_UNIT_GAP),
      ),
    );
    const elements: ElementDefinition[] = [
      {
        group: "nodes",
        data: {
          id: "__workflow-requirements-band__",
          visualLabel: "",
          width: geometry.totalWidth,
          height: geometry.workflowBandHeight,
        },
        position: { x: geometry.totalWidth / 2, y: geometry.workflowBandHeight / 2 },
        classes: "workflow-requirements-band",
        selectable: false,
        grabbable: false,
        locked: true,
      },
      {
        group: "nodes",
        data: {
          id: "__workflow-requirements-band-header__",
          visualLabel: "Workflow-wide status requirements · not a stage route",
          width: geometry.totalWidth,
          height: ROUTED_WORKFLOW_BAND_HEADER_HEIGHT,
        },
        position: { x: geometry.totalWidth / 2, y: ROUTED_WORKFLOW_BAND_HEADER_HEIGHT / 2 },
        classes: "workflow-requirements-band-header",
        selectable: false,
        grabbable: false,
        locked: true,
      },
    ];

    gates.forEach((gate, gateIndex) => {
      const row = Math.floor(gateIndex / gatesPerRow);
      const column = gateIndex % gatesPerRow;
      const rowGateCount = Math.min(gatesPerRow, gates.length - row * gatesPerRow);
      const rowWidth = rowGateCount * ROUTED_DECISION_UNIT_WIDTH
        + Math.max(0, rowGateCount - 1) * ROUTED_DECISION_UNIT_GAP;
      const rowLeft = (geometry.totalWidth - rowWidth) / 2;
      const center = {
        x: rowLeft + column * (ROUTED_DECISION_UNIT_WIDTH + ROUTED_DECISION_UNIT_GAP)
          + ROUTED_DECISION_UNIT_WIDTH / 2,
        y: ROUTED_WORKFLOW_BAND_HEADER_HEIGHT
          + row * (ROUTED_DECISION_UNIT_HEIGHT + ROUTED_DECISION_UNIT_GAP)
          + ROUTED_DECISION_UNIT_HEIGHT / 2,
      };
      elements.push(...this.decisionGateElements(
        gate,
        center,
        null,
        null,
        true,
        "workflow",
      ));
    });
    return elements;
  }

  private stepDecisionElements(
    node: WorkflowReadOnlyGraphNode,
    layoutNode: WorkflowGraphLayoutNode | undefined,
    nodeIndex: number,
    geometry: WorkflowGraphRoutedGeometry,
    ownerAuthorable: boolean,
  ): ElementDefinition[] {
    const gatesByLane = new Map<string, WorkflowReadOnlyGraphDecisionGate[]>();
    for (const gate of node.decisionGates ?? []) {
      const laneId = gate.responsibilityLaneId || FALLBACK_DECISION_LANE_ID;
      const gates = gatesByLane.get(laneId) ?? [];
      gates.push(gate);
      gatesByLane.set(laneId, gates);
    }
    const fallbackCenterY = geometry.contentOffsetY
      + nodeIndex * (ROUTED_DECISION_UNIT_HEIGHT + 24);
    const slotTop = layoutNode
      ? geometry.contentOffsetY + layoutNode.y
      : fallbackCenterY - ROUTED_DECISION_UNIT_HEIGHT / 2;
    const slotHeight = layoutNode?.height ?? ROUTED_DECISION_UNIT_HEIGHT;
    const elements: ElementDefinition[] = [];

    gatesByLane.forEach((gates, laneId) => {
      const lane = geometry.laneById.get(laneId)
        ?? geometry.laneById.get(FALLBACK_DECISION_LANE_ID);
      if (!lane) {
        return;
      }
      const stackHeight = gates.length * ROUTED_DECISION_UNIT_HEIGHT
        + Math.max(0, gates.length - 1) * ROUTED_DECISION_UNIT_GAP;
      gates.forEach((gate, gateIndex) => {
        const center = {
          x: lane.x,
          y: slotTop + (slotHeight - stackHeight) / 2
            + gateIndex * (ROUTED_DECISION_UNIT_HEIGHT + ROUTED_DECISION_UNIT_GAP)
            + ROUTED_DECISION_UNIT_HEIGHT / 2,
        };
        elements.push(...this.decisionGateElements(
          gate,
          center,
          node.id,
          node.sourceId,
          ownerAuthorable,
          node.id,
        ));
      });
    });
    return elements;
  }

  private decisionGateElements(
    gate: WorkflowReadOnlyGraphDecisionGate,
    unitCenter: { readonly x: number; readonly y: number },
    ownerRendererId: string | null,
    ownerSourceNodeId: string | null,
    ownerAuthorable: boolean,
    idScope: string,
  ): ElementDefinition[] {
    const gateKey = `${encodeURIComponent(idScope)}__${encodeURIComponent(gate.id)}`;
    const diamondId = `__workflow-decision-gateway__${gateKey}`;
    const matchedId = `__workflow-decision-matched__${gateKey}`;
    const fallbackId = `__workflow-decision-fallback__${gateKey}`;
    const guidanceId = `__workflow-decision-guidance__${gateKey}`;
    const unitLeft = unitCenter.x - ROUTED_DECISION_UNIT_WIDTH / 2;
    const unitTop = unitCenter.y - ROUTED_DECISION_UNIT_HEIGHT / 2;
    const decisionBodyCenterY = unitTop + ROUTED_DECISION_BODY_HEIGHT / 2;
    const diamondX = unitLeft + ROUTED_DECISION_DIAMOND_WIDTH / 2;
    const outcomeX = unitLeft + ROUTED_DECISION_DIAMOND_WIDTH
      + ROUTED_DECISION_ELEMENT_GAP
      + ROUTED_DECISION_OUTCOME_WIDTH / 2;
    const actionData = {
      ownerSourceNodeId,
      ownerRendererId,
      ownerAuthorable,
      decisionGateId: gate.id,
      decisionGateRequest: workflowDecisionGateRequest(gate),
    };
    const actionClasses = [
      "decision-action",
      gate.isInvalid ? "decision-action--invalid" : "",
      !ownerAuthorable ? "decision-action--repair" : "",
    ].filter(Boolean).join(" ");
    const elements: ElementDefinition[] = [
      {
        group: "nodes",
        data: {
          id: diamondId,
          visualLabel: workflowDecisionQuestionVisualLabel(gate),
          width: ROUTED_DECISION_DIAMOND_WIDTH,
          height: ROUTED_DECISION_DIAMOND_HEIGHT,
          ...actionData,
        },
        position: { x: diamondX, y: decisionBodyCenterY },
        classes: `decision-gateway ${actionClasses}`,
        selectable: false,
        grabbable: false,
        locked: true,
      },
      {
        group: "nodes",
        data: {
          id: matchedId,
          visualLabel: workflowDecisionMatchedVisualLabel(gate),
          width: ROUTED_DECISION_OUTCOME_WIDTH,
          height: gate.isInvalid
            ? ROUTED_DECISION_OUTCOME_HEIGHT * 1.6
            : ROUTED_DECISION_OUTCOME_HEIGHT,
          ...actionData,
        },
        position: {
          x: outcomeX,
          y: gate.isInvalid
            ? decisionBodyCenterY
            : decisionBodyCenterY
              - (ROUTED_DECISION_OUTCOME_HEIGHT + ROUTED_DECISION_OUTCOME_VERTICAL_GAP) / 2,
        },
        classes: `decision-outcome ${gate.isInvalid ? "decision-outcome--invalid" : "decision-outcome--matched"} ${actionClasses}`,
        selectable: false,
        grabbable: false,
        locked: true,
      },
      {
        group: "nodes",
        data: {
          id: guidanceId,
          visualLabel: workflowDecisionGuidanceVisualLabel(gate),
          width: ROUTED_DECISION_UNIT_WIDTH,
          height: ROUTED_DECISION_GUIDANCE_HEIGHT,
          ...actionData,
        },
        position: {
          x: unitCenter.x,
          y: unitTop + ROUTED_DECISION_BODY_HEIGHT
            + ROUTED_DECISION_GUIDANCE_GAP
            + ROUTED_DECISION_GUIDANCE_HEIGHT / 2,
        },
        classes: `decision-guidance ${actionClasses}`,
        selectable: false,
        grabbable: false,
        locked: true,
      },
      {
        group: "edges",
        data: {
          id: `__workflow-decision-match-flow__${gateKey}`,
          source: diamondId,
          target: matchedId,
          label: gate.isInvalid ? "" : `[${gate.matchedOutcomeLabel}]`,
        },
        classes: "decision-outcome-flow decision-non-routing",
        selectable: false,
        grabbable: false,
      },
    ];

    if (!gate.isInvalid) {
      elements.push(
        {
          group: "nodes",
          data: {
            id: fallbackId,
            visualLabel: workflowDecisionFallbackVisualLabel(gate),
            width: ROUTED_DECISION_OUTCOME_WIDTH,
            height: ROUTED_DECISION_OUTCOME_HEIGHT,
            ...actionData,
          },
          position: {
            x: outcomeX,
            y: decisionBodyCenterY
              + (ROUTED_DECISION_OUTCOME_HEIGHT + ROUTED_DECISION_OUTCOME_VERTICAL_GAP) / 2,
          },
          classes: `decision-outcome decision-outcome--fallback ${actionClasses}`,
          selectable: false,
          grabbable: false,
          locked: true,
        },
        {
          group: "edges",
          data: {
            id: `__workflow-decision-fallback-flow__${gateKey}`,
            source: diamondId,
            target: fallbackId,
            label: "[otherwise]",
          },
          classes: "decision-outcome-flow decision-non-routing",
          selectable: false,
          grabbable: false,
        },
      );
    }

    if (ownerRendererId) {
      elements.push({
        group: "edges",
        data: {
          id: `__workflow-decision-association__${gateKey}`,
          source: ownerRendererId,
          target: diamondId,
        },
        classes: "decision-association decision-non-routing",
        selectable: false,
        grabbable: false,
      });
    }
    return elements;
  }

  private handleElementTap(
    target: WorkflowGraphTapTarget,
    callbacks: WorkflowGraphSelectionCallbacks,
  ): void {
    if (target.hasClass("decision-summary-action")) {
      const ownerRendererId = target.data("ownerRendererId");
      const needsRepair = target.data("decisionScopeNeedsRepair") === true
        || target.data("ownerAuthorable") === false;
      if (
        needsRepair
        && typeof ownerRendererId === "string"
        && ownerRendererId.length > 0
      ) {
        callbacks.select(ownerRendererId);
      } else {
        const request = target.data("decisionScopeRequest");
        if (request) {
          callbacks.decisionScope?.(request as WorkflowReadOnlyGraphDecisionScopeRequest);
        } else if (typeof ownerRendererId === "string" && ownerRendererId.length > 0) {
          callbacks.select(ownerRendererId);
        }
      }
      return;
    }
    if (target.hasClass("decision-action")) {
      if (target.data("ownerAuthorable") === false) {
        callbacks.select(String(target.data("ownerRendererId")));
      } else {
        const request = target.data("decisionGateRequest");
        if (request) {
          callbacks.decisionGate?.(request as WorkflowReadOnlyGraphDecisionGateRequest);
        } else {
          const ownerRendererId = target.data("ownerRendererId");
          if (typeof ownerRendererId === "string" && ownerRendererId.length > 0) {
            callbacks.select(ownerRendererId);
          }
        }
      }
      return;
    }
    if (
      target.hasClass("decision-non-routing")
      || target.hasClass("swimlane-body")
      || target.hasClass("swimlane-header")
      || target.hasClass("workflow-requirements-band")
      || target.hasClass("workflow-requirements-band-header")
    ) {
      return;
    }

    callbacks.select(target.id());
  }

  private styles(
    palette: WorkflowGraphPalette = WORKFLOW_GRAPH_LIGHT_PALETTE,
  ): StylesheetStyle[] {
    return [
      {
        selector: "node",
        style: {
          "background-color": palette.nodeSurface,
          "border-color": palette.nodeBorder,
          "border-width": 1,
          "color": palette.nodeText,
          "font-size": 12,
          "font-weight": 700,
          "height": "data(height)",
          "label": "data(visualLabel)",
          "shape": "round-rectangle",
          "text-halign": "center",
          "text-max-width": "132px",
          "text-valign": "center",
          "text-wrap": "wrap",
          "width": "data(width)",
        },
      },
      {
        selector: "node.entry",
        style: {
          "background-color": palette.entrySurface,
          "border-color": palette.entryBorder,
          "shape": "ellipse",
        },
      },
      {
        selector: "node.human-step, node.automated-step",
        style: {
          "background-color": palette.nodeSurface,
          "border-color": palette.nodeBorder,
        },
      },
      {
        selector: "node.routing-decision",
        style: { shape: "diamond", "background-color": palette.terminalSurface, "border-color": palette.terminalBorder },
      },
      {
        selector: "node.terminal",
        style: {
          "background-color": palette.terminalSurface,
          "border-color": palette.terminalBorder,
          "border-width": 3,
        },
      },
      {
        selector: "node.swimlane-body",
        style: {
          "background-color": palette.laneSurface,
          "background-opacity": 0.82,
          "border-color": palette.laneBorder,
          "border-width": 1,
          "events": "no",
          "label": "",
          "shape": "rectangle",
          "z-compound-depth": "bottom",
          "z-index": -20,
        },
      },
      {
        selector: "node.swimlane-body--odd",
        style: {
          "background-color": palette.laneAlternateSurface,
        },
      },
      {
        selector: "node.swimlane-header",
        style: {
          "background-color": palette.laneHeaderSurface,
          "border-color": palette.laneHeaderBorder,
          "border-width": 1,
          "color": palette.laneHeaderText,
          "events": "no",
          "font-size": 11,
          "font-weight": 700,
          "shape": "rectangle",
          "text-max-width": "330px",
          "z-index": -10,
        },
      },
      {
        selector: "node.swimlane--stage",
        style: {
          "background-color": palette.stageLaneSurface,
          "border-color": palette.nodeBorder,
        },
      },
      {
        selector: "node.swimlane--needs-attention",
        style: {
          "background-color": palette.attentionLaneSurface,
          "border-color": palette.decisionBorder,
          "border-style": "dashed",
        },
      },
      {
        selector: "node.workflow-requirements-band",
        style: {
          "background-color": palette.decisionBandSurface,
          "border-color": palette.decisionAssociation,
          "border-width": 1,
          "events": "no",
          "label": "",
          "shape": "rectangle",
          "z-compound-depth": "bottom",
          "z-index": -20,
        },
      },
      {
        selector: "node.workflow-requirements-band-header",
        style: {
          "background-color": palette.decisionStrongSurface,
          "border-color": palette.decisionAssociation,
          "border-width": 1,
          "color": palette.decisionText,
          "events": "no",
          "font-size": 11,
          "font-weight": 700,
          "shape": "rectangle",
          "text-max-width": "360px",
          "z-index": -10,
        },
      },
      {
        selector: "node.decision-gateway",
        style: {
          "background-color": palette.decisionGatewaySurface,
          "border-color": palette.decisionStrongBorder,
          "border-width": 1.5,
          "color": palette.decisionText,
          "font-size": 11,
          "font-weight": 700,
          "shape": "diamond",
          "text-max-width": "76px",
        },
      },
      {
        selector: "node.decision-summary-action",
        style: {
          "background-color": palette.decisionSummarySurface,
          "border-color": palette.decisionBorder,
          "border-width": 1.5,
          "color": palette.decisionText,
          "font-size": 11,
          "font-weight": 700,
          "shape": "round-rectangle",
          "text-max-width": "164px",
        },
      },
      {
        selector: "node.decision-summary-action--workflow",
        style: {
          "background-color": palette.decisionStrongSurface,
          "border-width": 2,
        },
      },
      {
        selector: "node.decision-summary-action--invalid, node.decision-summary-action--repair",
        style: {
          "background-color": palette.invalidSurface,
          "border-color": palette.invalidBorder,
          "border-style": "dashed",
          "border-width": 2,
          "color": palette.invalidText,
        },
      },
      {
        selector: "node.decision-summary-action:active",
        style: {
          "border-width": 3,
          "overlay-color": palette.decisionBorder,
          "overlay-opacity": 0.14,
        },
      },
      {
        selector: "node.decision-outcome",
        style: {
          "background-color": palette.decisionOutcomeSurface,
          "border-color": palette.decisionBorder,
          "border-width": 1,
          "color": palette.decisionText,
          "font-size": 11,
          "font-weight": 600,
          "shape": "round-rectangle",
          "text-max-width": "142px",
        },
      },
      {
        selector: "node.decision-outcome--fallback",
        style: {
          "background-color": palette.decisionFallbackSurface,
          "border-color": palette.decisionFallbackBorder,
          "color": palette.decisionFallbackText,
        },
      },
      {
        selector: "node.decision-guidance",
        style: {
          "background-opacity": 0,
          "border-width": 0,
          "color": palette.decisionGuidanceText,
          "font-size": 11,
          "font-weight": 600,
          "shape": "rectangle",
          "text-max-width": "328px",
        },
      },
      {
        selector: "node.decision-action--invalid, node.decision-action--repair",
        style: {
          "border-color": palette.invalidBorder,
          "border-style": "dashed",
          "border-width": 2,
        },
      },
      {
        selector: "node.decision-action:active",
        style: {
          "border-width": 3,
          "overlay-color": palette.decisionBorder,
          "overlay-opacity": 0.14,
        },
      },
      {
        selector: "node.missing-endpoint, node.invalid",
        style: {
          "background-color": palette.invalidSurface,
          "border-color": palette.invalidBorder,
          "border-style": "dashed",
          "color": palette.invalidText,
        },
      },
      {
        selector: "node:selected",
        style: {
          "border-color": palette.selectionBorder,
          "border-width": 3,
          "overlay-color": palette.selectionOverlay,
          "overlay-opacity": 0.12,
        },
      },
      {
        selector: "edge",
        style: {
          "color": palette.edgeText,
          "curve-style": "taxi",
          "font-size": 10,
          "label": "",
          "line-color": palette.route,
          "target-arrow-color": palette.route,
          "target-arrow-shape": "triangle",
          "text-background-color": palette.edgeLabelSurface,
          "text-background-opacity": 0.9,
          "text-background-padding": "2px",
          "text-max-width": "132px",
          "text-rotation": "none",
          "text-wrap": "wrap",
          "width": 2,
        },
      },
      {
        selector: "edge.routed-outcome",
        style: { "label": "data(resultLabel)", "font-weight": 600 },
      },
      {
        selector: "edge.gated",
        style: {
          "line-color": palette.decisionStrongBorder,
          "line-style": "dashed",
          "target-arrow-color": palette.decisionStrongBorder,
        },
      },
      {
        selector: "edge.stage-path",
        style: {
          "arrow-scale": 1.35,
          "line-color": palette.stageRoute,
          "target-arrow-color": palette.stageRoute,
          "target-arrow-shape": "triangle",
          "taxi-direction": "downward",
          "width": 2.75,
        },
      },
      {
        selector: "edge.decision-association",
        style: {
          "curve-style": "taxi",
          "events": "no",
          "line-color": palette.decisionAssociation,
          "line-style": "dashed",
          "source-arrow-shape": "none",
          "target-arrow-shape": "none",
          "taxi-direction": "rightward",
          "width": 1.5,
        },
      },
      {
        selector: "edge.decision-outcome-flow",
        style: {
          "color": palette.decisionFlowText,
          "curve-style": "straight",
          "events": "no",
          "font-size": 11,
          "font-weight": 600,
          "label": "data(label)",
          "line-color": palette.decisionFlow,
          "target-arrow-color": palette.decisionFlow,
          "target-arrow-shape": "triangle",
          "text-background-color": palette.decisionOutcomeSurface,
          "text-background-opacity": 1,
          "text-background-padding": "1px",
          "text-max-width": "100px",
          "width": 1.25,
        },
      },
      {
        selector: "edge.reciprocal",
        style: {
          "control-point-distances": 48,
          "control-point-weights": 0.5,
          "curve-style": "unbundled-bezier",
        },
      },
      {
        selector: "edge.automated-parallel-outcome",
        style: {
          "control-point-distances": "data(automatedParallelOffset)",
          "control-point-weights": 0.5,
          "curve-style": "unbundled-bezier",
          "text-max-width": "108px",
        },
      },
      {selector:'edge.workflow-return',style:{'curve-style':'unbundled-bezier','control-point-distances':'data(returnOffset)','control-point-weights':0.5,'line-style':'dashed','font-size':10}},
      {selector:'edge.workflow-terminal-branch',style:{'taxi-turn':'100%'}},
      {selector:'edge.workflow-bypass',style:{'curve-style':'unbundled-bezier','control-point-distances':260,'control-point-weights':0.5}},
      {
        selector: "edge.invalid",
        style: {
          "line-color": palette.invalidBorder,
          "line-style": "dashed",
          "target-arrow-color": palette.invalidBorder,
        },
      },
      {
        selector: "edge:selected",
        style: {
          "line-color": palette.selectionBorder,
          "target-arrow-color": palette.selectionBorder,
          "width": 4,
        },
      },
    ];
  }
}
